import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { useToast } from '@/hooks/use-toast';

interface HoldingFormData {
  holding_name: string;
  holding_type: string;
  description: string;
  initial_value: number;
  current_value: number;
  acquisition_date: string;
  status: string;
  notes: string;
}

interface AddHoldingDialogProps {
  onHoldingAdded: () => void;
}

export const AddHoldingDialog: React.FC<AddHoldingDialogProps> = ({ onHoldingAdded }) => {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const { currentUser } = useUser();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<HoldingFormData>({
    defaultValues: {
      holding_type: 'Partnership',
      status: 'Active',
      initial_value: 0,
      current_value: 0,
      acquisition_date: new Date().toISOString().split('T')[0],
    },
  });

  const onSubmit = async (data: HoldingFormData) => {
    if (!currentUser?.id) return;

    setLoading(true);
    try {
      const { error } = await supabase.from('holdings').insert({
        profile_id: currentUser.id,
        holding_name: data.holding_name,
        holding_type: data.holding_type,
        description: data.description,
        initial_value: data.initial_value,
        current_value: data.current_value,
        acquisition_date: data.acquisition_date,
        status: data.status,
        notes: data.notes,
        performance_metrics: {
          roi: data.current_value > 0 && data.initial_value > 0 
            ? ((data.current_value - data.initial_value) / data.initial_value * 100).toFixed(2)
            : '0'
        }
      });

      if (error) throw error;

      toast({
        title: 'Holding Added',
        description: 'Your holding has been successfully added to your portfolio.',
      });

      reset();
      setOpen(false);
      onHoldingAdded();
    } catch (error) {
      console.error('Error adding holding:', error);
      toast({
        title: 'Error',
        description: 'Failed to add holding. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Holding
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[525px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Holding</DialogTitle>
          <DialogDescription>
            Add a new partnership, investment, or deal to your portfolio.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="holding_name">Holding Name</Label>
              <Input
                id="holding_name"
                placeholder="Nike Partnership"
                {...register('holding_name', { required: 'Holding name is required' })}
              />
              {errors.holding_name && (
                <p className="text-sm text-destructive">{errors.holding_name.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="holding_type">Type</Label>
              <Select
                value={watch('holding_type')}
                onValueChange={(value) => setValue('holding_type', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Partnership">Partnership</SelectItem>
                  <SelectItem value="Investment">Investment</SelectItem>
                  <SelectItem value="Sponsorship">Sponsorship</SelectItem>
                  <SelectItem value="Equity">Equity</SelectItem>
                  <SelectItem value="Real Estate">Real Estate</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Brief description of the holding..."
              {...register('description')}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="initial_value">Initial Value ($)</Label>
              <Input
                id="initial_value"
                type="number"
                step="0.01"
                placeholder="0.00"
                {...register('initial_value', { valueAsNumber: true })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="current_value">Current Value ($)</Label>
              <Input
                id="current_value"
                type="number"
                step="0.01"
                placeholder="0.00"
                {...register('current_value', { valueAsNumber: true })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="acquisition_date">Acquisition Date</Label>
              <Input
                id="acquisition_date"
                type="date"
                {...register('acquisition_date')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={watch('status')}
                onValueChange={(value) => setValue('status', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Paused">Paused</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Additional notes..."
              {...register('notes')}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Adding...' : 'Add Holding'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};